import React from 'react';

import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const BlogHero = () => {
  const featuredPost = {
    id: 1,
    title: "Building Scalable React Applications: Advanced Patterns for 2025",
    excerpt: `Explore cutting-edge React patterns that will transform your development workflow. From compound components to advanced state management strategies, learn how to build applications that scale with your team and user base.\n\nThis comprehensive guide covers performance optimization techniques, testing strategies, and architectural decisions that separate good React developers from great ones.`,
    author: "Adrian",
    publishedAt: "2025-01-05",
    readTime: "12 min read",
    difficulty: "Advanced",
    category: "Technical Tutorials",
    tags: ["React", "Architecture", "Performance", "Best Practices"],
    image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
    views: 2847,
    likes: 156,
    comments: 23,
    featured: true
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner':
        return 'bg-success/10 text-success border-success/20';
      case 'intermediate':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'advanced':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-accent/10 text-accent border-accent/20';
    }
  };

  return (
    <section className="relative bg-gradient-brand overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFD700' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            {/* Header */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 text-primary">
                  <Icon name="BookOpen" size={20} />
                  <span className="text-sm font-brand-medium">Featured Article</span>
                </div>
                <div className="h-1 w-12 bg-primary rounded-full" />
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-brand-headline text-white leading-tight">
                Knowledge &
                <span className="text-gradient-gold block">Insights Hub</span>
              </h1>
              
              <p className="text-lg text-white/80 font-brand-body max-w-xl">
                Deep technical insights, career growth strategies, and community building wisdom to accelerate your development journey.
              </p>
            </div>

            {/* Featured Post Preview */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="space-y-4">
                {/* Post Meta */}
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-brand-medium border ${getDifficultyColor(featuredPost?.difficulty)}`}>
                    {featuredPost?.difficulty}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-brand-medium bg-primary/20 text-primary border border-primary/30">
                    {featuredPost?.category}
                  </span>
                  <div className="flex items-center space-x-1 text-white/60 text-sm">
                    <Icon name="Clock" size={14} />
                    <span>{featuredPost?.readTime}</span>
                  </div>
                </div>

                {/* Post Title */}
                <h2 className="text-xl sm:text-2xl font-brand-subheading text-white leading-tight">
                  {featuredPost?.title}
                </h2>

                {/* Post Excerpt */}
                <p className="text-white/70 font-brand-body line-clamp-3">
                  {featuredPost?.excerpt?.split('\n')?.[0]}
                </p>

                {/* Post Stats */}
                <div className="flex items-center justify-between pt-4 border-t border-white/20">
                  <div className="flex items-center space-x-4 text-sm text-white/60">
                    <div className="flex items-center space-x-1">
                      <Icon name="Eye" size={14} />
                      <span>{featuredPost?.views?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Heart" size={14} />
                      <span>{featuredPost?.likes}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="MessageCircle" size={14} />
                      <span>{featuredPost?.comments}</span>
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ArrowRight"
                    iconPosition="right"
                    iconSize={14}
                    className="border-primary/30 text-primary hover:bg-primary/10"
                  >
                    Read More
                  </Button>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                iconName="Rss"
                iconPosition="left"
                iconSize={18}
                className="bg-primary text-primary-foreground hover:bg-primary/90 brand-shadow"
              >
                Subscribe to Newsletter
              </Button>
              <Button
                variant="outline"
                iconName="Search"
                iconPosition="left"
                iconSize={18}
                className="border-white/30 text-white hover:bg-white/10"
              >
                Explore All Posts
              </Button>
            </div>
          </div>

          {/* Featured Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden brand-shadow-lg">
              <Image
                src={featuredPost?.image}
                alt={featuredPost?.title}
                className="w-full h-80 lg:h-96 object-cover"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent" />
              
              {/* Post Info Overlay */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-white/80 text-sm">
                    <Icon name="User" size={16} />
                    <span>{featuredPost?.author}</span>
                    <span>•</span>
                    <span>{formatDate(featuredPost?.publishedAt)}</span>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {featuredPost?.tags?.slice(0, 3)?.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-md text-xs text-white font-brand-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Stats */}
            <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-4 brand-shadow-lg">
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-lg font-brand-subheading text-foreground">
                    {featuredPost?.views > 1000 ? `${(featuredPost?.views / 1000)?.toFixed(1)}k` : featuredPost?.views}
                  </div>
                  <div className="text-xs text-text-secondary">Views</div>
                </div>
                <div className="w-px h-8 bg-border" />
                <div className="text-center">
                  <div className="text-lg font-brand-subheading text-foreground">{featuredPost?.likes}</div>
                  <div className="text-xs text-text-secondary">Likes</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogHero;